export type Culture = 'en' | 'it' | 'fr' | 'de' | 'es';

interface Translations {
  cartButtonText: string;
  designPreview: string;
  redirectingToDesigner: string;
  complete: {
    partialTitle: (completed: number, total: number) => string;
    partialDescription: (total: number, itemsToComplete: number) => string;
    continueDesigning: string;
    title: string;
    description: string;
  };
  blocked: {
    title: string;
    description: string;
  };
  split: {
    title: string;
    description: string;
    useDesignFor: string;
    pieces: string;
    remainingDesigns: (remaining: number) => string;
    allPieces: string;
    confirm: string;
  };
}

const translationsByCulture: Record<Culture, Translations> = {
  en: {
    cartButtonText: 'Submit Design',
    designPreview: 'Design preview',
    redirectingToDesigner: 'Redirecting to designer...',
    complete: {
      partialTitle: (completed, total) => `Design successfully submitted for ${completed}/${total} items`,
      partialDescription: (total, itemsToComplete) =>
        `Your order contains ${total} items, and you still need to complete the design for ${itemsToComplete} items.`,
      continueDesigning: 'Click here to keep designing the remaining items in your order',
      title: 'Design successfully submitted',
      description: 'The design has been sent to the merchant.',
    },
    blocked: {
      title: 'The order is on hold',
      description: 'It is no longer possible to customize the product.',
    },
    split: {
      title: 'Is this artwork valid for all the items in your order?',
      description:
        'Since you bought multiple pieces of the same product, you can apply this design to all pieces or only some of them and create a new design.',
      useDesignFor: 'Use this design for',
      pieces: 'pieces.',
      remainingDesigns: remaining =>
        `After saving your choice, you will be able to create up to ${remaining} additional designs.`,
      allPieces: 'This design will be used for all the pieces purchased.',
      confirm: 'Confirm',
    },
  },
  it: {
    cartButtonText: 'Invia il design',
    designPreview: 'Anteprima del design',
    redirectingToDesigner: 'Reindirizzamento al designer...',
    complete: {
      partialTitle: (completed, total) => `Design inviato correttamente per ${completed}/${total} articoli`,
      partialDescription: (total, itemsToComplete) =>
        `Il tuo ordine contiene ${total} articoli e devi ancora completare il design per ${itemsToComplete} articoli.`,
      continueDesigning: 'Fai clic qui per continuare a progettare gli articoli rimanenti del tuo ordine',
      title: 'Design inviato correttamente',
      description: 'Il design è stato inviato al venditore.',
    },
    blocked: {
      title: "L'ordine è in sospeso",
      description: 'Non è più possibile personalizzare il prodotto.',
    },
    split: {
      title: 'Questa grafica è valida per tutti gli articoli del tuo ordine?',
      description:
        'Poiché hai acquistato più pezzi dello stesso prodotto, puoi applicare questo design a tutti i pezzi oppure solo ad alcuni e crearne uno nuovo.',
      useDesignFor: 'Usa questo design per',
      pieces: 'pezzi.',
      remainingDesigns: remaining =>
        `Dopo aver salvato la scelta, potrai creare fino a ${remaining} design aggiuntivi.`,
      allPieces: 'Questo design verrà utilizzato per tutti i pezzi acquistati.',
      confirm: 'Conferma',
    },
  },
  fr: {
    cartButtonText: 'Soumettre le design',
    designPreview: 'Aperçu du design',
    redirectingToDesigner: 'Redirection vers le designer...',
    complete: {
      partialTitle: (completed, total) => `Design envoyé avec succès pour ${completed}/${total} articles`,
      partialDescription: (total, itemsToComplete) =>
        `Votre commande contient ${total} articles et vous devez encore terminer le design de ${itemsToComplete} articles.`,
      continueDesigning: 'Cliquez ici pour continuer à concevoir les articles restants de votre commande',
      title: 'Design envoyé avec succès',
      description: 'Le design a été envoyé au vendeur.',
    },
    blocked: {
      title: 'La commande est en attente',
      description: "Il n'est plus possible de personnaliser le produit.",
    },
    split: {
      title: 'Ce visuel est-il valable pour tous les articles de votre commande ?',
      description:
        'Comme vous avez acheté plusieurs exemplaires du même produit, vous pouvez appliquer ce design à tous les exemplaires ou seulement à certains, puis créer un nouveau design.',
      useDesignFor: 'Utiliser ce design pour',
      pieces: 'exemplaires.',
      remainingDesigns: remaining =>
        `Après avoir enregistré votre choix, vous pourrez créer jusqu’à ${remaining} designs supplémentaires.`,
      allPieces: 'Ce design sera utilisé pour tous les exemplaires achetés.',
      confirm: 'Confirmer',
    },
  },
  de: {
    cartButtonText: 'Design einreichen',
    designPreview: 'Designvorschau',
    redirectingToDesigner: 'Weiterleitung zum Designer...',
    complete: {
      partialTitle: (completed, total) => `Design für ${completed}/${total} Artikel erfolgreich übermittelt`,
      partialDescription: (total, itemsToComplete) =>
        `Ihre Bestellung enthält ${total} Artikel und Sie müssen das Design noch für ${itemsToComplete} Artikel fertigstellen.`,
      continueDesigning: 'Klicken Sie hier, um die verbleibenden Artikel Ihrer Bestellung weiter zu gestalten',
      title: 'Design erfolgreich übermittelt',
      description: 'Das Design wurde an den Händler gesendet.',
    },
    blocked: {
      title: 'Die Bestellung ist pausiert',
      description: 'Das Produkt kann nicht mehr personalisiert werden.',
    },
    split: {
      title: 'Soll dieses Design für alle Artikel Ihrer Bestellung verwendet werden?',
      description:
        'Da Sie mehrere Exemplare desselben Produkts gekauft haben, können Sie dieses Design auf alle oder nur einige Exemplare anwenden und anschließend ein neues Design erstellen.',
      useDesignFor: 'Dieses Design verwenden für',
      pieces: 'Artikel.',
      remainingDesigns: remaining =>
        `Nach dem Speichern Ihrer Auswahl können Sie bis zu ${remaining} zusätzliche Designs erstellen.`,
      allPieces: 'Dieses Design wird für alle gekauften Artikel verwendet.',
      confirm: 'Bestätigen',
    },
  },
  es: {
    cartButtonText: 'Enviar diseño',
    designPreview: 'Vista previa del diseño',
    redirectingToDesigner: 'Redirigiendo al diseñador...',
    complete: {
      partialTitle: (completed, total) => `Diseño enviado correctamente para ${completed}/${total} artículos`,
      partialDescription: (total, itemsToComplete) =>
        `Tu pedido contiene ${total} artículos y todavía debes completar el diseño de ${itemsToComplete} artículos.`,
      continueDesigning: 'Haz clic aquí para seguir diseñando los artículos restantes de tu pedido',
      title: 'Diseño enviado correctamente',
      description: 'El diseño se ha enviado al vendedor.',
    },
    blocked: {
      title: 'El pedido está en espera',
      description: 'Ya no es posible personalizar el producto.',
    },
    split: {
      title: '¿Este diseño es válido para todos los artículos de tu pedido?',
      description:
        'Como has comprado varias unidades del mismo producto, puedes aplicar este diseño a todas las unidades o solo a algunas y crear un diseño nuevo.',
      useDesignFor: 'Usar este diseño para',
      pieces: 'unidades.',
      remainingDesigns: remaining =>
        `Después de guardar tu elección, podrás crear hasta ${remaining} diseños adicionales.`,
      allPieces: 'Este diseño se utilizará para todas las unidades compradas.',
      confirm: 'Confirmar',
    },
  },
};

const supportedCultures: Culture[] = ['it', 'fr', 'de', 'es'];

export const getPreferredCulture = (browserLanguage = navigator.language): Culture => {
  const language = browserLanguage.split(/[-_]/)[0].toLowerCase();

  return supportedCultures.includes(language as Culture) ? language as Culture : 'en';
};

export const getTranslations = (browserLanguage = navigator.language): Translations =>
  translationsByCulture[getPreferredCulture(browserLanguage)];
